import { useRef } from "react";
import { FormattedMessage } from "react-intl";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { DocumentView } from "@/components/documents/DocumentView";
import { Button } from "@/components/design/Button";
import type { IngestedFile } from "@/lib/file-ingest";
import { PROJECT_DOCUMENT_ACCEPT } from "@/lib/upload-limits";

// Ein Platz für Dokumente einer Bewerbung: das kanonische Ablagefeld (@adr SPARK-0022-file-drop-fields@4)
// mit den gewählten Dateien als Kacheln. Was aufgenommen wird, entscheidet der Dialog
// (application-documents.ts); der Platz reicht nur die Dateien weiter.
//
// Die Dateiliste wird SOFORT kopiert: das Feld leert sein <input> direkt nach der Übergabe, und im
// Browser leert das die live FileList gleich mit (jsdom tut das nicht, ein Test sähe es nicht).
export function ApplicationDocumentSlot({
  idPrefix,
  label,
  files,
  multiple,
  canAdd,
  disabled,
  onFiles,
  onRemove,
}: {
  idPrefix: string;
  /** Text, weil er auch das Ablagefeld benennt: zwei Felder mit derselben Aufforderung wären sonst gleichnamig. */
  label: string;
  files: readonly IngestedFile[];
  multiple: boolean;
  /** Ob noch eine Datei Platz hat; ohne Platz fehlt der Knopf zum Hinzufügen. */
  canAdd: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
  onRemove: (index: number) => void;
}) {
  const zoneRef = useRef<FileDropZoneHandle>(null);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="smycles-field-label">{label}</span>
        {multiple && files.length > 0 && canAdd ? (
          <Button variant="secondary" size="sm" disabled={disabled} onClick={() => zoneRef.current?.openPicker()}>
            + <FormattedMessage id="common.add" defaultMessage="Hinzufügen" />
          </Button>
        ) : null}
      </div>
      <FileDropZone
        ref={zoneRef}
        inputId={`${idPrefix}-file`}
        variant="zone"
        ariaLabel={label}
        accept={PROJECT_DOCUMENT_ACCEPT}
        multiple={multiple}
        disabled={disabled}
        clickToOpen={files.length === 0}
        onFiles={(list) => onFiles(Array.from(list))}
      >
        {files.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 p-3 @sm:grid-cols-4">
            {files.map((file, index) => (
              <DocumentView variant="tile"
                file={file}
                key={`${file.name}-${index}`}
                idPrefix={`${idPrefix}-${index}`}
                name={file.name}
                sizeBytes={file.size}
                previewUrl={file.mimeType.startsWith("image/") ? `data:${file.mimeType};base64,${file.content}` : null}
                onRemove={disabled ? undefined : () => onRemove(index)}
              />
            ))}
          </div>
        ) : null}
      </FileDropZone>
    </div>
  );
}
